import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { PaystubPreview } from "./PaystubPreview";
import { btnPrimary, btnGhost } from "../lib/ui";
import { toast } from "sonner";
import { Printer, Download, Pencil, CheckCircle2, Loader2 } from "lucide-react";

export const DeliveryStep = ({ form, paystubId, apiBase, revisionsRemaining, onRevise }) => {
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const printFrameRef = useRef(null);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${apiBase}/paystubs/${paystubId}/pdf`);
      if (!res.ok) throw new Error("Failed to fetch PDF");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "earnings_statement.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Could not download PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  // Print the generated PDF itself (not the on-screen preview) via a hidden iframe.
  const handlePrint = async () => {
    setPrinting(true);
    try {
      const res = await fetch(`${apiBase}/paystubs/${paystubId}/pdf`);
      if (!res.ok) throw new Error("Failed to fetch PDF");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const iframe = printFrameRef.current;
      iframe.src = url;
      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          window.open(url, "_blank");
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      };
      iframe.onerror = () => URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not open the PDF to print. Please try again.");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="mb-6 flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-[#ECFDF5] border border-[#10B981]/30 flex items-center justify-center">
          <CheckCircle2 className="w-6 h-6 text-[#10B981]" />
        </div>
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#0F172A]">
            Payment complete — your statement is ready
          </h2>
          <p className="text-[#475569] mt-1">Create your paystub, then print it or download the PDF. You receive it on screen; we do not email it.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-8 items-start">
        <div className="lg:col-span-2 lg:sticky lg:top-24 space-y-4">
          <div className="rounded-xl border border-[#D0E2FF] bg-white shadow-sm p-6">
            <div className="font-display font-semibold text-[#0F172A] mb-4">Output actions</div>
            <div className="space-y-3">
              <Button data-testid="download-pdf-button" onClick={handleDownload} disabled={downloading}
                className={`${btnPrimary} w-full rounded-full py-6`}>
                {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Download PDF
              </Button>
              <Button data-testid="print-button" onClick={handlePrint} disabled={printing} variant="outline"
                className={`${btnGhost} w-full rounded-full py-6`}>
                {printing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
                Print
              </Button>
            </div>
          </div>

          <div className="rounded-xl border border-[#D0E2FF] bg-[#F8FAFC] p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-[#0F172A]">Free revisions</span>
              <span data-testid="revisions-remaining"
                className={`text-sm font-semibold px-2 py-0.5 rounded-full ${
                  revisionsRemaining > 0 ? "bg-[#ECFDF5] text-[#10B981]" : "bg-slate-100 text-slate-400"
                }`}>
                {revisionsRemaining} left
              </span>
            </div>
            <p className="text-sm text-[#64748B] mb-4">
              You can edit the details once for free and regenerate your PDF.
            </p>
            <Button data-testid="revise-button" onClick={onRevise} disabled={revisionsRemaining <= 0}
              variant="outline" className={`${btnGhost} w-full`}>
              <Pencil className="w-4 h-4 mr-2" /> Edit &amp; regenerate
            </Button>
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="rounded-lg border border-slate-200 shadow-2xl ring-1 ring-black/5 overflow-hidden bg-white">
            <PaystubPreview form={form} watermark={false} />
          </div>
          <p className="text-center text-xs text-[#94A3B8] mt-3">
            On-screen preview. Use “Download PDF” or “Print” for the exact letter-size document.
          </p>
        </div>
      </div>
      <iframe ref={printFrameRef} title="print-frame" style={{ display: "none" }} />
    </motion.div>
  );
};
